/**
 * WormDigOverlay — Main orchestrator for the worm dig minigame.
 *
 * Layers (bottom to top):
 *   1. Dark background
 *   2. Worm sprites (hidden until revealed)
 *   3. Dirt canvas (scratch-off layer)
 *   4. Dirt particles (physics, above canvas)
 *   5. UI: "End Now" button, worm counter, timer
 *
 * Phases: 'active' → 'summary'
 */

import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { usePhysicsSimulation } from '@/hooks/usePhysicsSimulation';
import type { PhysicsBounds, PhysicsBody } from '@/utils/physics';
import type { WormDigConfig, WormDigResult, BuriedWorm } from '@/types';
import { placeWorms } from '@/utils/wormDig';
import { RevealTracker } from '@/utils/wormDig/RevealTracker';
import { DirtCanvas } from './DirtCanvas';
import { WormSprite } from './WormSprite';
import { WormDigSummary } from './WormDigSummary';

type Phase = 'active' | 'summary';

// Dirt particle physics config — lighter than collect overlay
const PARTICLE_PHYSICS = {
  gravity: 400,
  airDamping: 0.08,
  groundFriction: 0.7,
  angularDamping: 0.05,
  settleThreshold: 10,
  settleAngularThreshold: 5,
};

interface DirtParticle {
  id: string;
  spawnedAt: number;
}

let particleIdCounter = 0;

interface WormDigOverlayProps {
  config: WormDigConfig | null;
}

export function WormDigOverlay({ config }: WormDigOverlayProps) {
  const [phase, setPhase] = useState<Phase>('active');
  const [result, setResult] = useState<WormDigResult | null>(null);
  const [, setTick] = useState(0); // Force re-renders for worm reveals
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });

  const containerRef = useRef<HTMLDivElement>(null);
  const wormsRef = useRef<BuriedWorm[]>([]);
  const trackerRef = useRef<RevealTracker | null>(null);
  const startTimeRef = useRef(0);
  const wormsFoundRef = useRef(0);
  const particlesRef = useRef<DirtParticle[]>([]);
  const lastParticleSpawnRef = useRef(0);
  const particleRefsMap = useRef<Map<string, HTMLDivElement>>(new Map());
  const timeoutIdRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Physics bounds for dirt particles
  const bounds: PhysicsBounds | null = useMemo(() => {
    if (containerSize.width === 0) return null;
    return {
      left: 0,
      top: 0,
      right: containerSize.width,
      bottom: containerSize.height,
    };
  }, [containerSize]);

  const { addBody, removeBody, forceUpdate: physicsForceUpdate } = usePhysicsSimulation({
    bounds,
    config: PARTICLE_PHYSICS,
    onFrame: (snapshot) => {
      // Imperative DOM positioning for particles
      for (const body of snapshot.bodies) {
        const el = particleRefsMap.current.get(body.id);
        if (el) {
          el.style.transform = `translate(${body.position.x}px, ${body.position.y}px) rotate(${body.rotation}deg)`;
        }
      }
    },
    enabled: phase === 'active' && !!config,
  });

  // Initialize game when config changes
  useEffect(() => {
    if (!config) return;

    setPhase('active');
    setResult(null);
    wormsFoundRef.current = 0;
    startTimeRef.current = Date.now();
    particlesRef.current = [];
    particleRefsMap.current.clear();
    particleIdCounter = 0;
  }, [config]);

  // Measure container
  useEffect(() => {
    if (!config) return;

    const measure = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        setContainerSize({ width: rect.width, height: rect.height });
      }
    };

    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [config]);

  // Place worms once container is measured
  useEffect(() => {
    if (!config || containerSize.width === 0 || containerSize.height === 0) return;

    const worms = placeWorms(
      config.wormCount,
      containerSize.width,
      containerSize.height,
      100, // WORM_DIG_MIN_SPACING
      60,  // WORM_DIG_EDGE_PADDING
    );
    wormsRef.current = worms;

    trackerRef.current = new RevealTracker(
      containerSize.width,
      containerSize.height,
      worms,
      config.wormHitRadius,
      config.revealThreshold,
    );

    setTick((t) => t + 1);
  }, [config, containerSize]);

  // Timeout
  useEffect(() => {
    if (!config || config.timeoutMs <= 0 || phase !== 'active') return;

    timeoutIdRef.current = setTimeout(() => {
      finishGame(true);
    }, config.timeoutMs);

    return () => {
      if (timeoutIdRef.current) clearTimeout(timeoutIdRef.current);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config, phase]);

  const finishGame = useCallback(
    (endedEarly: boolean) => {
      if (!config || phase !== 'active') return;

      if (timeoutIdRef.current) {
        clearTimeout(timeoutIdRef.current);
        timeoutIdRef.current = null;
      }

      const digResult: WormDigResult = {
        wormsFound: wormsFoundRef.current,
        totalWorms: config.wormCount,
        baitAwarded: wormsFoundRef.current * config.wormsPerFind,
        durationMs: Date.now() - startTimeRef.current,
        endedEarly,
      };

      setResult(digResult);
      setPhase('summary');
    },
    [config, phase],
  );

  // Spawn dirt particles at dig position
  const spawnParticles = useCallback(
    (x: number, y: number) => {
      if (!config) return;

      const now = Date.now();
      if (now - lastParticleSpawnRef.current < 60) return;
      lastParticleSpawnRef.current = now;

      // Remove old particles
      const cutoff = now - 1500;
      const expired = particlesRef.current.filter((p) => p.spawnedAt < cutoff);
      for (const p of expired) {
        removeBody(p.id);
        particleRefsMap.current.delete(p.id);
      }
      particlesRef.current = particlesRef.current.filter((p) => p.spawnedAt >= cutoff);

      // Cap check
      if (particlesRef.current.length >= config.maxParticles) return;

      // Spawn 1-3 particles
      const count = 1 + Math.floor(Math.random() * 3);
      for (let i = 0; i < count; i++) {
        if (particlesRef.current.length >= config.maxParticles) break;

        const id = `dirt-${particleIdCounter++}`;
        const size = 4 + Math.random() * 8;

        const body: PhysicsBody = {
          id,
          position: { x, y },
          velocity: {
            x: (Math.random() - 0.5) * 300,
            y: -(100 + Math.random() * 200), // Upward
          },
          rotation: Math.random() * 360,
          angularVelocity: (Math.random() - 0.5) * 400,
          width: size,
          height: size,
          restitution: 0.3,
          friction: 1.0,
          settled: false,
        };

        addBody(body);
        particlesRef.current.push({ id, spawnedAt: now });
      }

      physicsForceUpdate();
    },
    [config, addBody, removeBody, physicsForceUpdate],
  );

  const handleDig = useCallback(
    (x: number, y: number) => {
      if (!config || phase !== 'active' || !trackerRef.current) return;

      const newlyRevealed = trackerRef.current.dig(x, y, config.digRadius);

      if (newlyRevealed.length > 0) {
        wormsFoundRef.current += newlyRevealed.length;
        setTick((t) => t + 1);

        for (const worm of newlyRevealed) {
          config.onWormFound?.(worm);
        }

        // Check if all worms found
        if (trackerRef.current.allRevealed()) {
          // Small delay so player sees the last reveal animation
          setTimeout(() => finishGame(false), 600);
        }
      }

      spawnParticles(x, y);
    },
    [config, phase, finishGame, spawnParticles],
  );

  const handleEndNow = useCallback(() => {
    finishGame(true);
  }, [finishGame]);

  const handleDismiss = useCallback(() => {
    if (result && config) {
      config.onComplete(result);
    }
  }, [result, config]);

  if (!config) return null;

  return (
    <div
      className="fixed inset-0 z-50 select-none"
      style={{ touchAction: 'none' }}
    >
      {/* CSS animations */}
      <style>{`
        @keyframes wormFoundFloat {
          0% { transform: translate(-50%, 0); opacity: 1; }
          100% { transform: translate(-50%, -30px); opacity: 0; }
        }
        @keyframes wormSummaryPop {
          0% { transform: scale(0.8); opacity: 0; }
          100% { transform: scale(1); opacity: 1; }
        }
      `}</style>

      {/* Dark background with earthy tint */}
      <div className="absolute inset-0 bg-amber-950/90" />

      {/* Game container */}
      <div ref={containerRef} className="absolute inset-0 overflow-hidden">
        {containerSize.width > 0 && phase === 'active' && (
          <>
            {/* Layer 1: Worm sprites (beneath dirt) */}
            {wormsRef.current.map((worm) => (
              <WormSprite
                key={worm.id}
                x={worm.x}
                y={worm.y}
                rotation={worm.rotation}
                revealed={worm.revealed}
                baitPerWorm={config.wormsPerFind}
              />
            ))}

            {/* Layer 2: Dirt canvas (scratch-off) */}
            <DirtCanvas
              width={containerSize.width}
              height={containerSize.height}
              brushRadius={config.digRadius}
              onDig={handleDig}
              enabled={phase === 'active'}
            />

            {/* Layer 3: Dirt particles (above canvas) */}
            <div className="absolute inset-0 pointer-events-none">
              {particlesRef.current.map((p) => (
                <div
                  key={p.id}
                  ref={(el) => {
                    if (el) particleRefsMap.current.set(p.id, el);
                  }}
                  className="absolute rounded-full"
                  style={{
                    width: 6,
                    height: 6,
                    left: 0,
                    top: 0,
                    backgroundColor: `hsl(25, ${40 + Math.random() * 20}%, ${25 + Math.random() * 15}%)`,
                  }}
                />
              ))}
            </div>
          </>
        )}
      </div>

      {/* UI overlay */}
      {phase === 'active' && (
        <div className="absolute inset-x-0 top-0 p-4 flex justify-between items-start pointer-events-none z-10">
          {/* Worm counter */}
          <div className="bg-gray-900/80 border border-amber-500/50 rounded-lg px-4 py-2 pointer-events-none">
            <p className="text-amber-300 font-bold text-lg">
              🪱 {wormsFoundRef.current} / {config.wormCount}
            </p>
            <p className="text-gray-400 text-xs">Worms Found</p>
          </div>

          {/* End Now button */}
          <button
            onClick={handleEndNow}
            className="bg-red-700/80 hover:bg-red-600 border border-red-500/50 rounded-lg px-4 py-2 font-bold text-white text-sm transition-colors pointer-events-auto"
          >
            End Now
          </button>
        </div>
      )}

      {/* Instruction hint at bottom */}
      {phase === 'active' && wormsFoundRef.current === 0 && (
        <div className="absolute inset-x-0 bottom-8 flex justify-center pointer-events-none z-10">
          <div className="bg-gray-900/70 border border-gray-600/50 rounded-lg px-4 py-2">
            <p className="text-gray-300 text-sm">Click and drag to dig through the dirt!</p>
          </div>
        </div>
      )}

      {/* Summary phase */}
      {phase === 'summary' && result && (
        <WormDigSummary result={result} onDismiss={handleDismiss} />
      )}
    </div>
  );
}
